import { PAGE_REVEAL_TIMEOUT_MS } from '$lib/components/constants';

import { getContext, setContext } from 'svelte';
import { derived, readable, writable } from 'svelte/store';

import type { Readable } from 'svelte/store';

/**
 * CAP-4418: Holds back the blocks that sit *after* a sandboxed AI embed whose
 * height is not known yet, so nothing is ever painted in a position it is about
 * to move away from.
 *
 * A sandboxed embed mounts at height 0 and only allocates its real height once
 * the cross-origin sandbox has loaded, injected the embed and measured it. Every
 * block below it therefore renders at the top of the page first and jumps down
 * when the height arrives — the "CTA buttons appear before the AI block content"
 * report, on a page that stacks AI block and button sections.
 *
 * The previous attempt at this (FUN-3908) reserved a viewport-sized placeholder
 * for the embed instead. That only moves the problem: layout shift is symmetric,
 * so collapsing a 900px placeholder down to a 165px embed shifts the content
 * below it upwards exactly as far as growing from 0 shifted it down. Measured on
 * the reporting funnel, extending that placeholder to nested blocks roughly
 * doubled CLS (0.87-1.33 -> 1.64-2.05) rather than fixing it. Nothing short of an
 * accurate height helps, and the block data carries no height to reserve.
 *
 * What does work is not painting the moving content at all: an element that is
 * inserted, or revealed, at its final position never registers as a shift. So
 * blocks past the first pending embed are rendered but kept `visibility: hidden`
 * — rendered, because the embeds have to load and measure in parallel (holding
 * them back would reveal the page one embed at a time), and hidden rather than
 * removed, because an unpainted element cannot shift.
 */

/** Document-order index of every block on the current page, by block id. */
const blockDocumentOrder = writable<Map<string, number>>(new Map());

/** Ids of the AI embeds on the current page that have not reported a height yet. */
const pendingEmbedIds = writable<Set<string>>(new Set());

/**
 * Releases everything regardless of pending embeds. A sandbox that never loads
 * must not leave the lower half of a funnel invisible, so the hold is always
 * bounded — past the deadline the page behaves exactly as it did before.
 */
const hasRevealDeadlinePassed = writable(false);

let revealDeadlineTimeout: ReturnType<typeof setTimeout> | undefined;

type ComponentNode = {
    _id?: string;
    componentType?: string;
    content?: { provider?: string; head?: string; body?: string };
    components?: ComponentNode[];
};

/**
 * Whether a block is a sandboxed AI embed that is going to mount at height 0 and
 * grow — the only kind of block the page waits for.
 *
 * `Embed.View` renders nothing at all for an embed with neither head nor body,
 * so such a block would never report a height and would hold the page until the
 * deadline. It is excluded here rather than left to the deadline.
 */
const isPendingAiEmbed = (node: ComponentNode): boolean =>
    node.componentType === 'embed' &&
    node.content?.provider === 'ai' &&
    !!(node.content?.head || node.content?.body);

/**
 * Index every block of a page tree in depth-first pre-order, which is the order
 * the blocks appear in the DOM.
 *
 * Pre-order is what makes a single numeric comparison enough to express "render
 * this block": a container is always indexed before its children, so an ancestor
 * of the pending embed compares lower and keeps rendering, while its later
 * siblings compare higher and stay hidden.
 */
export const indexBlocksInDocumentOrder = (
    components: ComponentNode[] | undefined,
): Map<string, number> => {
    const order = new Map<string, number>();

    const walk = (nodes: ComponentNode[] | undefined) => {
        if (!Array.isArray(nodes)) {
            return;
        }

        for (const node of nodes) {
            if (node._id !== undefined && !order.has(node._id)) {
                order.set(node._id, order.size);
            }

            walk(node.components);
        }
    };

    walk(components);

    return order;
};

/**
 * Collect the AI embeds a page is going to have to wait for.
 *
 * Read from the page data rather than reported by the embed components as they
 * mount, because by then it is too late: `Transition` only renders its slot once
 * it has mounted, so an embed does not exist — and cannot announce itself —
 * until after every block on the page has already been created and started its
 * entrance. Seeding from the data means the hold is known before the first
 * block renders.
 */
const collectPendingAiEmbedIds = (components: ComponentNode[] | undefined): Set<string> => {
    const ids = new Set<string>();

    const walk = (nodes: ComponentNode[] | undefined) => {
        if (!Array.isArray(nodes)) {
            return;
        }

        for (const node of nodes) {
            if (node._id !== undefined && isPendingAiEmbed(node)) {
                ids.add(node._id);
            }

            walk(node.components);
        }
    };

    walk(components);

    return ids;
};

/**
 * Point the store at a new page. Any embed still pending belongs to the page
 * being left, so the state is dropped wholesale rather than reconciled.
 */
export const startPageReveal = (components: ComponentNode[] | undefined): void => {
    blockDocumentOrder.set(indexBlocksInDocumentOrder(components));
    pendingEmbedIds.set(collectPendingAiEmbedIds(components));
    hasRevealDeadlinePassed.set(false);

    clearTimeout(revealDeadlineTimeout);
    revealDeadlineTimeout = setTimeout(
        () => hasRevealDeadlinePassed.set(true),
        PAGE_REVEAL_TIMEOUT_MS,
    );
};

/** Test seam — production code clears the timeout via the next `startPageReveal`. */
export const stopPageReveal = (): void => {
    clearTimeout(revealDeadlineTimeout);
    revealDeadlineTimeout = undefined;
};

export const resolvePendingEmbed = (blockId: string): void => {
    pendingEmbedIds.update((ids) => {
        if (!ids.has(blockId)) {
            return ids;
        }

        const remaining = new Set(ids);
        remaining.delete(blockId);

        return remaining;
    });
};

/**
 * Document-order index of the first block that must stay hidden, i.e. the first
 * pending embed. The embed itself is revealed — it is the block whose height
 * everything below is waiting on, and it does not move while it grows.
 */
const firstPendingIndex = derived(
    [blockDocumentOrder, pendingEmbedIds, hasRevealDeadlinePassed],
    ([order, pendingIds, deadlinePassed]) => {
        if (deadlinePassed || pendingIds.size === 0) {
            return Infinity;
        }

        let first = Infinity;

        for (const id of pendingIds) {
            const index = order.get(id);

            if (index !== undefined && index < first) {
                first = index;
            }
        }

        return first;
    },
);

/**
 * Whether a block may be painted yet. Blocks the page tree does not index (a
 * nested component rendered outside the block list, say) are always revealed —
 * the hold is an optimisation, never a gate on content being shown at all.
 */
export const isBlockRevealed = derived(
    [blockDocumentOrder, firstPendingIndex],
    ([order, pendingIndex]) =>
        (blockId: string): boolean => {
            if (pendingIndex === Infinity) {
                return true;
            }

            const index = order.get(blockId);

            return index === undefined || index <= pendingIndex;
        },
);

/**
 * CAP-4418: The reveal state of the nearest enclosing block, so that a block's
 * entrance animation can wait for the block to actually be on screen.
 *
 * `Transition` runs its entrance the moment it mounts. For a held block that is
 * a second or so before anyone can see it, so the animation is over by the time
 * the block is revealed and it appears abruptly instead of fading in. Passing
 * the state down by context rather than by prop keeps every `Transition` inside
 * a block covered — list items, slider items and media answers each render their
 * own, several levels below `Block`.
 */
const BLOCK_REVEAL_CONTEXT = Symbol('blockReveal');

/** Outside a block — a consent overlay, a story — nothing is ever held. */
const ALWAYS_REVEALED: Readable<boolean> = readable(true);

export const setBlockRevealContext = (isRevealed: Readable<boolean>): void => {
    setContext(BLOCK_REVEAL_CONTEXT, isRevealed);
};

export const getBlockRevealContext = (): Readable<boolean> =>
    getContext<Readable<boolean> | undefined>(BLOCK_REVEAL_CONTEXT) ?? ALWAYS_REVEALED;
